/**
 * ISSUE-1366. The web composer's emoji picker data and the one text edit it
 * performs, kept pure so both are testable without a DOM.
 *
 * A browser on a computer has no emoji keyboard a fan reliably knows about,
 * which is why v1's `emoji_select_sheet.dart` exists. This is a curated set
 * leaning toward what a pool chat actually sends (sports, celebrating,
 * trash talk), not the full Unicode list. A phone browser still has its own
 * emoji keyboard on top of this.
 */

export type typeEmojiGroup = {
  key: string;
  label: string;
  emoji: readonly string[];
};

export const COMPOSER_EMOJI_GROUPS: readonly typeEmojiGroup[] = [
  {
    key: "faces",
    label: "Faces",
    emoji: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "🤣",
      "😂",
      "🙂",
      "😉",
      "😊",
      "😇",
      "🥰",
      "😍",
      "🤩",
      "😘",
      "😋",
      "😜",
      "🤪",
      "😝",
      "🤑",
      "🤗",
      "🤭",
      "🤫",
      "🤔",
      "🤐",
      "🤨",
      "😐",
      "😑",
      "😶",
      "😏",
      "😒",
      "🙄",
      "😬",
      "😌",
      "😔",
      "😪",
      "😴",
      "😷",
      "🤒",
      "🥵",
      "🥶",
      "🤯",
      "🤠",
      "🥳",
      "😎",
      "🤓",
      "🧐",
      "😕",
      "😟",
      "😮",
      "😲",
      "🥺",
      "😢",
      "😭",
      "😱",
      "😤",
      "😡",
      "🤬",
      "😈",
      "💀",
      "🤡",
      "👻",
      "🙈",
    ],
  },
  {
    key: "hands",
    label: "Hands",
    emoji: [
      "👍",
      "👎",
      "👊",
      "✊",
      "🤛",
      "🤜",
      "👏",
      "🙌",
      "👐",
      "🤲",
      "🤝",
      "🙏",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "👌",
      "🤌",
      "👈",
      "👉",
      "👆",
      "👇",
      "☝️",
      "✋",
      "🤚",
      "👋",
      "🤙",
      "💪",
      "🫡",
      "🫶",
      "👀",
      "🧠",
    ],
  },
  {
    key: "sports",
    label: "Sports",
    emoji: [
      "🏈",
      "🏀",
      "⚾",
      "🥎",
      "⚽",
      "🏒",
      "🏑",
      "🥍",
      "🏐",
      "🎾",
      "⛳",
      "🏌️",
      "🏎️",
      "🏁",
      "🥊",
      "🏆",
      "🥇",
      "🥈",
      "🥉",
      "🏅",
      "🎖️",
      "🎯",
      "🎲",
      "📈",
      "📉",
      "⏱️",
      "📺",
      "🏟️",
      "🧢",
      "👟",
      "🦬",
      "🦅",
    ],
  },
  {
    key: "party",
    label: "Celebrate",
    emoji: [
      "🎉",
      "🎊",
      "🥳",
      "🍾",
      "🥂",
      "🍻",
      "🍺",
      "🎈",
      "🎁",
      "🔥",
      "💯",
      "✨",
      "⭐",
      "🌟",
      "💥",
      "⚡",
      "🚀",
      "👑",
      "💰",
      "💸",
      "🤑",
      "🙃",
      "🫠",
      "😮‍💨",
    ],
  },
  {
    key: "food",
    label: "Food",
    emoji: [
      "🍕",
      "🍔",
      "🌭",
      "🌮",
      "🌯",
      "🍟",
      "🍗",
      "🍖",
      "🥩",
      "🥓",
      "🧀",
      "🥨",
      "🍿",
      "🥜",
      "🍩",
      "🍪",
      "🎂",
      "🍰",
      "🍦",
      "🥤",
      "☕",
      "🧃",
      "🍷",
      "🥃",
    ],
  },
  {
    key: "symbols",
    label: "Symbols",
    emoji: [
      "❤️",
      "🧡",
      "💛",
      "💚",
      "💙",
      "💜",
      "🖤",
      "🤍",
      "💔",
      "❤️‍🔥",
      "💕",
      "✅",
      "❌",
      "⭕",
      "❗",
      "❓",
      "‼️",
      "⚠️",
      "🚫",
      "💤",
      "🔒",
      "🔔",
      "📣",
      "🗓️",
    ],
  },
];

/**
 * Replaces the selected range of `text` with `insert` and returns the new
 * text with the caret placed right after what was inserted. A selection
 * that runs past the text, or is reversed, is clamped rather than trusted.
 */
export function insertAtSelection(input: {
  text: string;
  selectionStart: number;
  selectionEnd: number;
  insert: string;
}): { text: string; caret: number } {
  const length = input.text.length;
  const start = Math.max(0, Math.min(input.selectionStart, length));
  const end = Math.max(start, Math.min(input.selectionEnd, length));
  const next =
    input.text.slice(0, start) + input.insert + input.text.slice(end);
  return { text: next, caret: start + input.insert.length };
}

/**
 * The photo files in a paste or a drop, in order. Anything that is not an
 * image is left alone, so pasting a copied PDF or a text file does nothing
 * here; type and size are then checked by `validatePhotoSelection`.
 */
export function imageFilesFrom(
  files: Iterable<File> | null | undefined,
): File[] {
  if (files === null || files === undefined) return [];
  return Array.from(files).filter((file) => file.type.startsWith("image/"));
}

/** True when a drag carries files at all (not a text or link drag). */
export function isFileDrag(
  types: readonly string[] | null | undefined,
): boolean {
  return types?.includes("Files") ?? false;
}
